import { LitElement, html, css, nothing, type CSSResultGroup, type PropertyValues } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { publicQuoteRequestUrl } from '../utils/public-routes.js';

/**
 * A name for one submission, unique to this browser and this attempt.
 *
 * `crypto.randomUUID` is there on every browser that can run this bundle over https (and over
 * http://localhost, which counts as a secure context); the fallbacks exist for the one deployment
 * shape where it is not — a page served over plain http — because the alternative is a customer
 * whose retry silently files a second request.
 */
function newIdempotencyKey(): string {
    const c = globalThis.crypto;
    if (typeof c?.randomUUID === 'function') return c.randomUUID();
    if (typeof c?.getRandomValues === 'function') {
        const bytes = new Uint8Array(16);
        c.getRandomValues(bytes);
        return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
    }
    return `k${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
}

/** One engine verdict about the configuration, as the public wire carries it. */
export interface QuoteBlockingIssue {
    code: string;
    message: string;
    blocking: boolean;
}

/**
 * What the endpoint answers with when it has accepted a request.
 *
 * `reopenUrl` is filled in because requests are persisted (issue 04) and SHOWN because the route it
 * points at now serves them (issue 05). Until that route existed it was deliberately hidden: a
 * "spara den här länken" that opens an empty calculator is worse than no link.
 */
interface QuoteReceipt {
    reference: string;
    status: string;
    reopenUrl?: string | null;
}

type Phase = 'closed' | 'form' | 'sending' | 'sent' | 'failed';

/**
 * "Begär offert" — the button, the contact form and the success state on the public calculator page.
 *
 * <p><b>What it sends.</b> The configuration as the ENGINE holds it: `inputs` verbatim, the same
 * dictionary the evaluate endpoint consumes, plus who to answer. Not a rendered summary and not a
 * flattened list of the visible fields — see QuoteRequestSubmission on the server for why that
 * shape is the one that can be replayed, and issue 04 for what still has to be decided about
 * freezing it.</p>
 *
 * <p><b>What it does not do.</b> It does not compute, format or interpret a price. The number the
 * customer is looking at is the calculator's; this component's whole job is to carry the
 * configuration and the contact details to a person, and to be honest about whether that
 * worked.</p>
 *
 * <p>Its own element and file (one component per file). Public-bundle safe: no @umbraco-cms
 * imports — only UUI primitives, which the standalone bundle registers itself.</p>
 */
@customElement('ixit-quote-request-form')
export class IxitQuoteRequestForm extends LitElement {
    static styles: CSSResultGroup = css`
        :host {
            display: block;
            max-width: 1400px;
            margin: 0 auto;
            /* The side gutter moved to the two cards below (their margin), because a container
               query can restyle a container's descendants but never the container itself — and
               this host is the container. On a narrow surface the gutter has to shrink with the
               preview's, or the calculator's cards indent 10 px and the offer card 20. */
            padding-bottom: 48px;
            /* Measured, not assumed: at a 360 px surface in a 1707 px window — which is what an
               embedded calculator inside .site-container looks like — the old
               @media (max-width: 640px) never fired and the form laid out two 131 px columns.
               A media query measures the WINDOW; this form's width is set by whatever column the
               marketing page put it in. */
            container-type: inline-size;
            container-name: dof-quote;
            /* Fara som TEXT, samma härledning som previewbasens: --uui-color-danger är en
               fyllnadsfärg och mätte 4,24:1 på den ljusa ytan, precis under AA. Definierad HÄR och
               inte ärvd, av samma skäl som paletten nedan: en egenskap från en annan bunt är inte
               tillförlitligt på plats när detta element ritas första gången. */
            --dof-danger-text: color-mix(in srgb, var(--uui-color-danger-standalone, var(--uui-color-danger)) 72%, var(--uui-color-text));
            /* Both themes from ONE declaration, inline on this host: the standalone page has no
               Umbraco palette, and CSS custom properties injected by another bundle are not
               reliably there when this element first paints. Values mirror the preview base's dark
               palette so the two halves of the page cannot disagree about what dark looks like. */
            color: var(--uui-color-text, #1f2937);
        }
        :host([data-theme='dark']) {
            --uui-color-surface: #1b1b1f;
            --uui-color-surface-alt: #26262c;
            --uui-color-text: #e7e7ea;
            --uui-color-text-alt: #b2b2ba;
            --uui-color-border: #3a3a44;
            --uui-color-primary: #6ea8fe;
            --uui-color-positive: #4ade80;
            --uui-color-danger: #f87171;
            --uui-color-warning: #fbbf24;
            /* På denna grund ÄR den ljusa fyllnadstokenen det läsbara svaret — samma val som
               previewbasen gör, så de två halvorna av sidan inte kan säga olika. */
            --dof-danger-text: var(--uui-color-danger);
            --uui-color-interactive: #e7e7ea;
            --uui-color-default: #c7c7cf;
            --uui-color-default-contrast: #1b1b1f;
        }
        .qr-card,
        .qr-success {
            margin: 0 20px;
        }
        .qr-card {
            border: 1px solid var(--uui-color-border, #d1d5db);
            border-radius: 8px;
            background: var(--uui-color-surface-alt, #f9fafb);
            padding: 20px;
        }
        .qr-lead {
            margin: 0 0 12px;
            color: var(--uui-color-text-alt, #6b7280);
            font-size: 0.95rem;
        }
        .qr-fields {
            display: grid;
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 16px;
        }
        .qr-field.qr-wide {
            grid-column: 1 / -1;
        }
        .qr-field {
            display: flex;
            flex-direction: column;
            gap: 4px;
        }
        .qr-label {
            font-size: 0.9rem;
            font-weight: 500;
        }
        .qr-label .qr-required {
            color: var(--dof-danger-text);
            margin-left: 2px;
        }
        .qr-error {
            color: var(--dof-danger-text);
            font-size: 0.85rem;
        }
        .qr-actions {
            display: flex;
            align-items: center;
            gap: 12px;
            margin-top: 16px;
            flex-wrap: wrap;
        }
        .qr-success {
            border: 1px solid var(--uui-color-positive, #16a34a);
            /* A translucent tint, not a pale fill: a solid light green is unreadable against the
               adaptive text colour in dark mode. */
            background: rgba(34, 197, 94, 0.14);
            border-radius: 8px;
            padding: 20px;
        }
        .qr-success h2 {
            margin: 0 0 6px;
            font-size: 1.15rem;
        }
        .qr-reference {
            font-family: ui-monospace, monospace;
            font-weight: 600;
        }
        .qr-reopen {
            font-family: ui-monospace, monospace;
            font-size: 0.85rem;
            /* Breaks inside the token rather than pushing the card wide: the link is 60-odd
               characters of base64url and no phone is that wide. */
            overflow-wrap: anywhere;
            color: var(--uui-color-interactive, #1f2937);
        }
        .qr-changed {
            margin-top: 12px;
        }
        .qr-blocked {
            border-left: 4px solid var(--uui-color-warning, #b45309);
            background: rgba(180, 83, 9, 0.14);
            border-radius: 6px;
            padding: 12px 14px;
            margin-bottom: 12px;
        }
        .qr-blocked ul {
            margin: 6px 0 0;
            padding-left: 18px;
        }
        .qr-blocked p {
            margin: 6px 0 0;
        }
        .qr-title {
            margin: 0 0 8px;
            font-size: 1.15rem;
        }
        .qr-success p {
            margin: 0 0 8px;
        }
        .qr-success p:last-child {
            margin: 0;
        }
        .qr-send-error {
            color: var(--dof-danger-text);
            font-size: 0.9rem;
        }
        /* ---- Narrow surfaces (mobilvy-preview/03) ----
           Last in the sheet on purpose: .qr-success sets its padding further down, and a container
           query adds no specificity to win with. */
        @container dof-quote (max-width: 560px) {
            .qr-fields {
                grid-template-columns: minmax(0, 1fr);
            }
            /* The same gutter and the same shrink as the preview's own frames, so the two halves of
               the page keep one rhythm instead of indenting differently. */
            .qr-card,
            .qr-success {
                margin: 0 10px;
                padding: 12px;
            }
        }
    `;

    /** The configurator this configuration belongs to — the id in the page's own URL. */
    @property({ type: String }) configuratorId = '';

    /** The live input dictionary, handed straight on. Never re-keyed here. */
    @property({ type: Object }) inputs: Record<string, unknown> = {};

    /** The engine's verdicts. A blocking one means "do not quote this", and the form says so. */
    @property({ type: Array }) issues: QuoteBlockingIssue[] = [];

    @state() private _phase: Phase = 'closed';
    @state() private _name = '';
    @state() private _email = '';
    @state() private _company = '';
    @state() private _message = '';
    /** Field key → what is wrong with it, in Swedish. Empty until the customer has tried to send. */
    @state() private _fieldErrors: Record<string, string> = {};
    @state() private _reference = '';
    @state() private _sendError = '';
    /** The link back into this configuration, as the receipt gave it. Empty until one has arrived. */
    @state() private _reopenUrl = '';
    /**
     * The customer has changed the configuration since the request went off. What is on screen is
     * then no longer what we were sent, and the success card says so and offers the one honest way
     * out — sending the changed configuration as a NEW request.
     */
    @state() private _changedSinceSent = false;

    /**
     * The name this submission goes by. Minted on the first send and REUSED by every retry of the
     * same thing, so a request whose response was lost is answered with its original receipt instead
     * of being filed twice.
     *
     * <p>Cleared the moment anything about the submission changes — a contact field here, or the
     * configuration behind the form (see `updated`). That is the half that is easy to leave out: a
     * key that outlives an edit would have the server answer with the receipt for what the customer
     * sent BEFORE the edit, and the edit would be silently lost. Same key means "the same request
     * again", and it must never mean "a different request that happens to follow it".</p>
     */
    private _idempotencyKey = '';

    private _darkModeMql: MediaQueryList | null = null;

    /** Mirrors the preview base: the CUSTOMER's OS preference decides, not a backoffice setting. */
    private _detectTheme = () => {
        const dark = window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false;
        this.setAttribute('data-theme', dark ? 'dark' : 'light');
    };

    connectedCallback() {
        super.connectedCallback();
        this._detectTheme();
        this._darkModeMql = window.matchMedia?.('(prefers-color-scheme: dark)') ?? null;
        this._darkModeMql?.addEventListener?.('change', this._detectTheme);
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this._darkModeMql?.removeEventListener?.('change', this._detectTheme);
        this._darkModeMql = null;
    }

    /**
     * The configuration changed under the open form, so whatever is sent next is a different
     * request. The host only re-assigns `inputs` when a value actually changed (it spreads a new
     * object per change), so this does not churn on re-renders.
     *
     * <p>After a send it also reopens the door. The success card used to be terminal: a customer who
     * kept configuring — which is exactly what somebody who followed a reopen link from our own mail
     * does — had no way to send the new configuration without reloading the page, and the button
     * that would have done it was no longer rendered. The key is cleared either way, so the next send
     * is a new request rather than a retry of the answered one.</p>
     *
     * <p><b>willUpdate, not updated.</b> It sets reactive state, and state set AFTER a render is a
     * second render Lit warns about — the affordance would appear one frame late, which is exactly
     * long enough for a test (and a fast customer) to look for it and not find it.</p>
     */
    protected willUpdate(changed: PropertyValues) {
        if (!changed.has('inputs')) return;
        this._invalidateIdempotencyKey();
        if (this._phase === 'sent') this._changedSinceSent = true;
    }

    private _invalidateIdempotencyKey() {
        this._idempotencyKey = '';
    }

    /**
     * The engine's verdicts that this configuration cannot become a business document.
     *
     * <p>They are shown as CONTEXT and they do not stop the send, which is the opposite of where this
     * started. A customer the engine refuses to price is precisely the customer who needs a person:
     * disabling the button leaves them with a number we would not stand behind, no explanation they
     * can act on, and no way to reach us — the feature inverted. Nothing extra goes on the wire
     * either; `inputs` reproduces the blocked state on replay, so whoever picks the request up sees
     * exactly what the customer saw.</p>
     */
    private get _blockingIssues(): QuoteBlockingIssue[] {
        return (this.issues ?? []).filter((i) => i?.blocking);
    }

    /**
     * The same rules the server enforces (QuoteRequestContact), stated here so the customer is told
     * which box is wrong instead of being handed a 400. The server keeps its copy regardless — this
     * one is the polite half, not the true half.
     */
    private _validate(): Record<string, string> {
        const errors: Record<string, string> = {};
        if (!this._name.trim()) errors.name = 'Fyll i ditt namn.';
        const email = this._email.trim();
        if (!email) {
            errors.email = 'Fyll i din e-postadress.';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            // A shape check, matching the server's. Whether an address exists is settled by a mail
            // arriving at it, never by a regex.
            errors.email = 'E-postadressen ser inte riktig ut.';
        }
        if (this._message.length > 4000) errors.message = 'Meddelandet är för långt (max 4000 tecken).';
        return errors;
    }

    private async _submit() {
        const errors = this._validate();
        this._fieldErrors = errors;
        if (Object.keys(errors).length > 0) return;

        this._phase = 'sending';
        this._sendError = '';
        if (!this._idempotencyKey) this._idempotencyKey = newIdempotencyKey();
        try {
            const response = await fetch(publicQuoteRequestUrl(this.configuratorId), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    configuratorId: this.configuratorId,
                    // Verbatim. The point of the contract is that what comes back out goes back in.
                    inputs: this.inputs ?? {},
                    contact: {
                        name: this._name.trim(),
                        email: this._email.trim(),
                        company: this._company.trim(),
                        message: this._message,
                    },
                    idempotencyKey: this._idempotencyKey,
                }),
            });

            if (!response.ok) {
                // No server prose: the route answers with a code, and a proxy could answer with
                // anything at all. What the customer needs is that it did not arrive and that there
                // is another way to reach us.
                this._phase = 'failed';
                this._sendError = response.status === 429
                    ? 'För många försök just nu. Vänta en minut och försök igen.'
                    : 'Förfrågan kunde inte skickas. Försök igen om en stund.';
                return;
            }

            const receipt = (await response.json()) as QuoteReceipt;
            this._reference = receipt?.reference ?? '';
            this._reopenUrl = receipt?.reopenUrl ?? '';
            // This configuration IS what was just sent, whatever was true before the send.
            this._changedSinceSent = false;
            this._phase = 'sent';
        } catch {
            this._phase = 'failed';
            this._sendError = 'Förfrågan kunde inte skickas — kontrollera din uppkoppling och försök igen.';
        }
    }

    render() {
        if (this._phase === 'sent') return this._renderSuccess();
        if (this._phase === 'closed') return this._renderTrigger();
        return this._renderForm();
    }

    private _renderTrigger() {
        return html`
            <div class="qr-card">
                <p class="qr-lead">
                    Vill du ha ett skarpt pris eller har du frågor om konfigurationen? Skicka den till
                    oss så återkommer vi.
                </p>
                <uui-button
                    look="primary"
                    label="Begär offert"
                    @click=${() => { this._phase = 'form'; }}
                >Begär offert</uui-button>
            </div>
        `;
    }

    private _renderForm() {
        const blocking = this._blockingIssues;
        const sending = this._phase === 'sending';
        return html`
            <div class="qr-card">
                <h2 class="qr-title">Begär offert</h2>
                <p class="qr-lead">
                    Din konfiguration följer med förfrågan. Vi återkommer med besked — du behöver inte
                    fylla i något mer om verktyget här.
                </p>

                ${blocking.length > 0 ? html`
                    <div class="qr-blocked">
                        <strong>Vi behöver titta på den här konfigurationen manuellt</strong>
                        <ul>${blocking.map((i) => html`<li>${i.message}</li>`)}</ul>
                        <p>Skicka in den ändå — din konfiguration följer med
                           och vi återkommer med besked.</p>
                    </div>
                ` : nothing}

                <div class="qr-fields">
                    ${this._renderTextField('name', 'Namn', true, this._name, (v) => { this._name = v; })}
                    ${this._renderTextField('email', 'E-post', true, this._email, (v) => { this._email = v; })}
                    ${this._renderTextField('company', 'Företag', false, this._company, (v) => { this._company = v; })}
                    <div class="qr-field qr-wide">
                        <label class="qr-label" for="qr-message">Meddelande</label>
                        <uui-textarea
                            id="qr-message"
                            label="Meddelande"
                            rows="4"
                            .value=${this._message}
                            @input=${(e: Event) => {
                                e.stopPropagation();
                                this._message = (e.target as HTMLTextAreaElement).value;
                                this._invalidateIdempotencyKey();
                            }}
                        ></uui-textarea>
                        ${this._fieldErrors.message
                            ? html`<span class="qr-error">${this._fieldErrors.message}</span>`
                            : nothing}
                    </div>
                </div>

                <div class="qr-actions">
                    <uui-button
                        look="primary"
                        label="Skicka förfrågan"
                        ?disabled=${sending}
                        @click=${() => this._submit()}
                    >${sending ? 'Skickar…' : 'Skicka förfrågan'}</uui-button>
                    <uui-button
                        look="secondary"
                        label="Avbryt"
                        ?disabled=${sending}
                        @click=${() => { this._phase = 'closed'; this._fieldErrors = {}; }}
                    >Avbryt</uui-button>
                    ${this._sendError ? html`<span class="qr-send-error">⚠ ${this._sendError}</span>` : nothing}
                </div>
            </div>
        `;
    }

    /**
     * One text row. UUI's input fires its own `input` event from inside its shadow root; it is
     * stopped here before we re-read the value, so a host listening for `input` on this element does
     * not also see the inner one and read a different target.
     */
    private _renderTextField(
        key: string,
        label: string,
        required: boolean,
        value: string,
        assign: (v: string) => void,
    ) {
        const error = this._fieldErrors[key];
        return html`
            <div class="qr-field">
                <label class="qr-label" for="qr-${key}">
                    ${label}${required ? html`<span class="qr-required" aria-hidden="true">*</span>` : nothing}
                </label>
                <uui-input
                    id="qr-${key}"
                    label=${label}
                    type=${key === 'email' ? 'email' : 'text'}
                    ?required=${required}
                    .value=${value}
                    @input=${(e: Event) => {
                        e.stopPropagation();
                        assign((e.target as HTMLInputElement).value);
                        this._invalidateIdempotencyKey();
                    }}
                ></uui-input>
                ${error ? html`<span class="qr-error">${error}</span>` : nothing}
            </div>
        `;
    }

    private _renderSuccess() {
        return html`
            <div class="qr-success">
                <h2>Tack — vi återkommer!</h2>
                <p>
                    Din förfrågan är mottagen tillsammans med konfigurationen. Vi hör av oss till
                    ${this._email} med besked.
                </p>
                ${this._reference
                    ? html`<p>Referens: <span class="qr-reference">${this._reference}</span></p>`
                    : nothing}
                ${this._renderReopenLink()}
                ${this._changedSinceSent ? html`
                    <p class="qr-changed">
                        Du har ändrat konfigurationen efter att förfrågan skickades. Skicka den
                        ändrade konfigurationen som en ny förfrågan — den vi redan har ligger kvar.
                    </p>
                    <uui-button
                        look="primary"
                        label="Skicka den ändrade konfigurationen"
                        @click=${() => { this._phase = 'form'; }}
                    >Skicka den ändrade konfigurationen</uui-button>
                ` : nothing}
            </div>
        `;
    }

    /**
     * The way back into this configuration.
     *
     * <p>Absolute, built from the origin the browser is already on: the receipt's URL is
     * site-relative on purpose (an absolute one assembled server-side from a proxied request's
     * headers is how a link ends up naming a container), and a customer copying a link out of a page
     * needs the whole thing. `window.location.origin` is the one origin we know is right, because it
     * is the one that served this page.</p>
     *
     * <p>Said plainly to be a capability. The token in it is what opens the configuration, so
     * whoever holds the link can open it — that is what makes it useful in a mail thread and what
     * makes it worth one sentence of warning.</p>
     */
    private _renderReopenLink() {
        if (!this._reopenUrl) return nothing;
        let href = this._reopenUrl;
        try {
            href = new URL(this._reopenUrl, window.location.origin).href;
        } catch {
            // A receipt whose link we cannot resolve is shown as it arrived rather than not at all.
        }
        return html`
            <p>
                Spara den här länken om du vill öppna konfigurationen igen — den som har länken kan
                se den, så dela den bara med den du vill ska se den:
            </p>
            <p><a class="qr-reopen" href=${href}>${href}</a></p>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'ixit-quote-request-form': IxitQuoteRequestForm;
    }
}
